import {
  Button,
  Form,
  FormGroup,
  Modal,
  ModalVariant,
  TextInput,
} from '@patternfly/react-core'
import { ExclamationCircleIcon } from '@patternfly/react-icons'
import { CloseOutline } from '@styled-icons/evaicons-outline/CloseOutline'
import * as React from 'react'
import { useCallback, useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import styled from 'styled-components'
import { border, layout, space } from 'styled-system'

import { Role } from '@app/domain/iam/user.interface'
import * as Auth from '@app/state/authenticate'
import { useValidatePin } from '@app/state/user/hooks'
import { mapValidatePinStatus } from '@app/state/user/interfaces'

import { HeaderScrollTextContainer } from './content-layout'
import { ScrollUp } from './controls'

const PageBackground = styled.div`
  display: flex;
  flex-flow: column;
  align-items: center;
  background-color: ${(props) => props.theme.colors.primary};
  overflow-y: auto;
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  margin: 0;
  padding: 0;
`

const Container = styled.div<Record<string, unknown>>`
  ${space}
  ${layout}
  ${border}
  position: relative;
  display: flex;
  flex-direction: column;
  background-color: white;
  border-radius: 25px;
`

const Header = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  margin-bottom: 0.5rem;
  position: sticky;
  top: 0;
  z-index: 10;
  background-color: white;
  border-radius: 25px;
`

const Toolbar = styled.div`
  width: 100%;
  display: flex;
  color: ${(props) => props.theme.colors.primary};
  align-items: center;
`

const ExitButton = styled.button`
  border: none;
  outline: none;
  background: ${(props) => props.theme.colors.primary};
  line-height: 25px;
  font-weight: 500;
  border-radius: 50%;
  border: 2px solid ${(props) => props.theme.colors.primary};
  color: #fff;
  text-align: center;
  padding: 0px;
  text-decoration: none;
  height: 45px;
  width: 45px;
  transition: all 0.3s ease;
  margin: 20px;
  box-shadow: rgb(0 0 0 / 22%) 0px 5px 5px -2px,
    rgb(0 0 0 / 20%) 0px 7px 8px 0px, rgb(0 0 0 / 15%) 0px 5px 12px 0px;
`

const ProgressBar = styled.div<{ progress: number }>`
  flex: 3 1 auto;

  h3 {
    text-align: center;
  }

  .meter {
    width: 100%;
    height: 20px; /* Can be anything */
    position: relative;
    background-color: ${(props) => props.theme.colors.gray};
    -moz-border-radius: 25px;
    -webkit-border-radius: 25px;
    border-radius: 25px;
    box-shadow: inset 0 -1px 1px rgba(255, 255, 255, 0.3);
  }

  .meter > span {
    display: block;
    height: 100%;
    width: ${(props) => props.progress}%;
    z-index: 10000;
    border-radius: 25px;
    background-color: ${(props) => props.theme.colors.secondary};
    background-image: linear-gradient(
      center bottom,
      ${(props) => props.theme.colors.primary} 37%,
      ${(props) => props.theme.colors.secondary} 69%
    );
    box-shadow: inset 0 2px 9px rgba(255, 255, 255, 0.3),
      inset 0 -2px 6px rgba(0, 0, 0, 0.4);
    position: relative;
    overflow: hidden;
  }
`

const Spacer = styled.div`
  width: 45px;
  margin: 20px;
`

interface Props {
  children?: unknown
  instrument?: string
  onExit: (pin: string) => void
  progress?: number
}

const containerWidth = { xs: 1, sm: 1, md: '90%', lg: '90%', xl: 768 }
const containerMarginY = { xs: 0, sm: 0, md: 4, lg: 4 }
const scrollTolerance = 5

export const Page = function ({
  children,
  instrument,
  onExit,
  progress,
}: Props) {
  const dispatch = useDispatch()
  React.useEffect(() => {
    dispatch(Auth.actions.assumeRole(Role.Patient))
  }, [dispatch])

  const [isExitModalOpen, setIsExitModalOpen] = useState<boolean>(false)
  const [userPin, setUserPin] = useState<string>('')

  const [validated, validatePin] = useValidatePin()

  const [scrollAtTop, setScrollAtTop] = useState(true)

  const handleScroll = useCallback(() => {
    const scrollParent = document.querySelector('.page-background')
    setScrollAtTop(scrollParent.scrollTop <= scrollTolerance)
  }, [])

  const onExitModalToggle = useCallback(() => {
    setIsExitModalOpen(!isExitModalOpen)
  }, [isExitModalOpen])

  const handleExit = useCallback(() => {
    validatePin(userPin).then((isValid) => {
      if (isValid) {
        onExit(userPin)
      }
    })
  }, [userPin, onExit, validatePin])

  const onSubmit = useCallback((event) => {
    event.preventDefault()
  }, [])

  useEffect(() => {
    handleScroll()
  }, [handleScroll])

  return (
    <>
      <PageBackground
        className="page-background"
        onScroll={handleScroll}
        onMouseEnter={handleScroll}
        onTouchStart={handleScroll}
      >
        <Container
          className="container"
          width={containerWidth}
          marginY={containerMarginY}
        >
          <Header id="header">
            <Toolbar>
              {onExit && (
                <ExitButton onClick={onExitModalToggle}>
                  <CloseOutline size="25" color={'#FFF'} />
                </ExitButton>
              )}
              {progress !== undefined && (
                <ProgressBar progress={progress}>
                  <h3>Your Progress ({progress}%)</h3>
                  <div className="meter">
                    <span />
                  </div>
                  {instrument && <h3>{instrument}</h3>}
                </ProgressBar>
              )}
              <Spacer />
            </Toolbar>
            {!scrollAtTop ? (
              <HeaderScrollTextContainer>
                <ScrollUp />
                Scroll Up for More
              </HeaderScrollTextContainer>
            ) : null}
          </Header>
          {children}
        </Container>
      </PageBackground>
      <Modal
        variant={ModalVariant.small}
        title="Security PIN Required"
        isOpen={isExitModalOpen}
        onClose={onExitModalToggle}
        actions={[
          <Button key="confirm" variant="primary" onClick={handleExit}>
            Confirm
          </Button>,
          <Button key="cancel" variant="link" onClick={onExitModalToggle}>
            Cancel
          </Button>,
        ]}
      >
        <Form isHorizontal onSubmit={onSubmit}>
          <FormGroup
            label="PIN"
            isRequired
            fieldId="security-pin"
            helperText="Please enter your security PIN"
            helperTextInvalid="Incorrect PIN. Please try again."
            helperTextInvalidIcon={<ExclamationCircleIcon />}
            validated={mapValidatePinStatus(validated)}
          >
            <TextInput
              id="security-pin"
              type="password"
              value={userPin}
              onChange={setUserPin}
              validated={mapValidatePinStatus(validated)}
            />
          </FormGroup>
        </Form>
      </Modal>
    </>
  )
}

export const PageFill = styled.div`
  flex: 1 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
`

export const PageFillContent = styled.div`
  margin: 2rem;
  padding: 2rem;
  border-radius: 0.5rem;

  .title {
    font-weight: bold;
  }
`

type PageLoadingProps = {
  title?: string
  className?: string // make sure to include this
  children?: React.ReactNode
}

const UnstyledPageLoading = ({ className, title }: PageLoadingProps) => (
  <PageFill className={className}>
    <PageFillContent>
      {title ? <div className="title">{title}</div> : null}
      <div>Loading ...</div>
    </PageFillContent>
  </PageFill>
)

export const PageLoading = styled(UnstyledPageLoading)`
  ${PageFillContent} {
    background-color: ${(props) => props.theme.colors.gray};
  }
`

type PageErrorProps = {
  title?: string
  className?: string // make sure to include this
  children?: React.ReactNode
}

const UnstyledPageError = ({ className, title, children }: PageErrorProps) => (
  <PageFill className={className}>
    <PageFillContent>
      {title ? <div className="title">{title}</div> : null}
      <div>{children}</div>
    </PageFillContent>
  </PageFill>
)

export const PageError = styled(UnstyledPageError)`
  ${PageFillContent} {
    background-color: ${({ theme }) => theme.colors.warning};
  }
`
